import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HubIcon from '@mui/icons-material/Hub';
import SaveIcon from '@mui/icons-material/Save';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import { useSnackbar } from 'notistack';

import { api } from '../../services/api';
import AnalyzerHeader from './AnalyzerHeader';
import NumericParamField from './NumericParamField';
import ScoreCard from './ScoreCard';
import NeighborDataChipConnected from './NeighborDataChipConnected';
import { NeighborDataIncompleteCard } from './NeighborDataStatus';
import { useAnalyserGate } from './useAnalyserGate';
import { useNeighborData } from './NeighborDataContext';
import SegmentMapView, { segmentColorForIndex } from './SegmentMapView';
import { isJnet2CapOrNode } from '../../utils/channelMapUtils';
import { normalizeSerial } from '../../utils/powerMapUtils';
import {
  clearLoadedSegmentProposal,
  persistLoadedSegmentProposal,
  readLoadedSegmentProposal,
} from '../../utils/segmentProposalUtils';

const DEFAULT_MAX_HOP_DEPTH = 4;
const MIN_MAX_HOP_DEPTH = 1;
const MAX_MAX_HOP_DEPTH = 32;

const DEFAULT_MIN_RSSI = -80;
const MIN_MIN_RSSI = -80;
const MAX_MIN_RSSI = -40;

const DEFAULT_HYSTERESIS_PCT = 0;
const MIN_HYSTERESIS_PCT = 0;
// Effective ceiling: the backend clamps the bonus to penalty/(1-penalty)
// to keep the best-first heap monotonic. With the fixed 0.15 hop penalty
// that is ~17.6%, so the field caps at 17 to avoid silent clamping.
const MAX_HYSTERESIS_PCT = 17;

const MAX_HOP_DEPTH_TOOLTIP = 'Hard cap on chain depth. The algorithm will not attach a lamp more than this many hops from its CAP; a lamp reachable only via a longer chain is left unassigned (you can still assign it manually below). Lower it (e.g. 2) to force shallower, CAP-centric segments. Default 4.';
const MIN_RSSI_TOOLTIP = 'Membership RSSI gate (dBm). A lamp is only attached to a segment when every hop of its path is at least this strong; a lamp reachable only via weaker links is left unassigned (you can still assign it manually below). Raise it (e.g. -75) to keep marginal links out of segments. Default -80 (the RF floor); values below -80 snap back to -80.';
const HYSTERESIS_TOOLTIP = 'Stability bias (%) toward a lamp\'s CURRENT serving CAP, to damp run-to-run churn from RF jitter. 0 (default) means no bias — lamps are assigned purely on link merit. A small value (e.g. 10%) only breaks near-ties; a materially better CAP still wins. Capped at ~17% to keep the ranking stable.';
const AFFINITY_TOOLTIP = 'Path rank score for this attachment option (higher = better). Parent-path affinity × this link\'s RF quality, with ~15% penalty per hop — shallow, strong paths beat deep or weak ones. Options are sorted best-first within each segment.';

// Short, friendly labels for the backend "not_assignable_code" reasons —
// why a heard link never became an eligible backhaul candidate for the
// auto-planner. The full backend detail string is shown as the tooltip.
const NOT_ASSIGNABLE_LABELS = {
  slot_blocked: 'Parent slot full',
  bssid_no_match: 'No matching parent slot',
  bssid_ambiguous: 'Ambiguous parent slot',
  no_attach_slot: 'No parent slot for band/ch',
  missing_band: 'Scan missing band',
  below_rssi_floor: 'Below RF floor (TX-adj.)',
  below_snr_floor: 'SNR below floor',
  missing_rssi_down: 'No downlink RSSI',
  no_parent: 'Parent missing',
};

const notAssignableLabel = (code) => NOT_ASSIGNABLE_LABELS[code] || 'Not attachable';

// v2 decision labels — why a lamp landed where it did under the
// capacity-aware + interference-aware planner.
const BINDING_CONSTRAINT_LABELS = {
  capacity: 'Moved for capacity',
  search: 'Moved (interference/headroom)',
  headroom: 'No backup path',
  none: '',
};

const HEADROOM_TOOLTIP = 'Path headroom toward this CAP from the v2 planner: how many relay-diverse parents reach the CAP within ~30% of the best path. 0 = single point of failure (no backup), so optimize has no room to re-parent this lamp if its link degrades.';
const MOVED_TOOLTIP = 'The v2 planner relocated this lamp off the greedy seed: "capacity" = its seed radio was over its usable slot budget; "interference/headroom" = the global search found a less-crowded / higher-headroom parent at comparable cost.';
const MARGINAL_TOOLTIP = 'Close call: a second CAP was within ~15% of the chosen CAP\'s path cost (or this lamp has no backup path). Worth an operator\'s eyes before applying.';
const CEILING_TOOLTIP = 'Per-segment path-cost ceiling (estimate-time proxy for the optimize throughput ceiling): median and worst member path cost to the CAP, plus how many members have no backup path. Lower median/worst and fewer fragile members = more room for optimize to work.';
const RSSI_RAW_TOOLTIP = 'Raw downlink (STA_SNR + STA_NF) from the golden neighbor snapshot row that matched this attach (band + channel). Compare the same band/channel in Neighbors — live reports can differ from golden until you mark neighbors golden.';
const RSSI_ADJ_TOOLTIP = 'TX-adjusted downlink: raw scan normalized to the parent\'s current TX on the attach slot. The planner uses this for the min-RSSI gate and affinity scoring.';

const formatRssi = (dbm) => {
  if (dbm == null || dbm === undefined || !Number.isFinite(dbm)) return '—';
  return `${Math.round(dbm)} dBm`;
};

const formatBandLink = (band, channel) => {
  if (!band) return '—';
  return channel ? `${band} ch${channel}` : band;
};

const buildExistingSegmentBySerial = (trees) => {
  const m = new Map();
  (Array.isArray(trees) ? trees : trees?.trees || []).forEach((t) => {
    const capSerial = (t.caps || [])[0] || '';
    const info = {
      treeId: t.id,
      name: t.name || `Segment ${shortSerial(capSerial)}`,
      segmentId: t.segment_id,
      capSerial,
    };
    [...(t.devices || []), ...(t.caps || [])].forEach((s) => {
      const key = normalizeSerial(s);
      if (key) m.set(key, info);
    });
  });
  return m;
};

const treeHasSerial = (tree, serial) => {
  if (!tree) return false;
  const key = normalizeSerial(serial);
  return [...(tree.devices || []), ...(tree.caps || [])].some((s) => normalizeSerial(s) === key);
};

const treeSegmentInfo = (tree) => {
  const capSerial = (tree.caps || [])[0] || '';
  return {
    treeId: tree.id,
    name: tree.name || `Segment ${shortSerial(capSerial)}`,
    segmentId: tree.segment_id,
    capSerial,
  };
};

// Registered segment the lamp is on, other than targetTree (the segment
// Apply writes to). A multi-CAP segment counts as one segment: a lamp
// already on targetTree is not in another segment.
const otherExistingSegment = (existingSegmentBySerial, serial, targetTree) => {
  if (treeHasSerial(targetTree, serial)) return null;
  const info = existingSegmentBySerial.get(normalizeSerial(serial));
  if (!info) return null;
  if (targetTree && info.treeId === targetTree.id) return null;
  return info;
};

const buildExistingTreesByCap = (trees) => {
  const m = new Map();
  (Array.isArray(trees) ? trees : trees?.trees || []).forEach((t) => {
    (t.caps || []).forEach((cap) => {
      const key = normalizeSerial(cap);
      if (!key) return;
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(t);
    });
  });
  return m;
};

// Compares a proposed segment with the registered segment of the same CAP.
// Devices the plan leaves out (unassigned, outside the cohort, or proposed
// for another CAP) are never removed here: a device moving to another
// segment is released from this one when that segment's rollout completes.
const diffProposedSegment = (seg, manualAdds, existingTreesByCap) => {
  const cap = normalizeSerial(seg.cap_serials?.[0]);
  const proposed = Array.from(new Set(
    [...(seg.members || []).map((m) => m.serial), ...(manualAdds || [])]
      .map(normalizeSerial)
      .filter(Boolean),
  ));
  const candidates = existingTreesByCap.get(cap) || [];
  let tree = null;
  let bestOverlap = -1;
  candidates.forEach((t) => {
    const members = new Set((t.devices || []).map(normalizeSerial));
    const overlap = proposed.filter((s) => members.has(s)).length;
    if (overlap > bestOverlap) {
      bestOverlap = overlap;
      tree = t;
    }
  });
  if (!tree) {
    return { tree: null, toAdd: proposed, unchanged: false };
  }
  const members = new Set((tree.devices || []).map(normalizeSerial));
  const toAdd = proposed.filter((s) => !members.has(s));
  return { tree, toAdd, unchanged: toAdd.length === 0 };
};

const AUDIT_MODE_ORPHANS = 'orphans';
const AUDIT_MODE_AP_WILDCARDS = 'ap_wildcards';
const AUDIT_MODE_UNASSIGNED = 'unassigned';
const AUDIT_MODE_MEMBERSHIP = 'membership';

const MEMBERSHIP_CONFIG_ONLY = 'config_only';
const MEMBERSHIP_KEEP_DEVICE = 'device';
const MEMBERSHIP_KEEP_TREE = 'tree';
const MEMBERSHIP_SKIP = 'skip';

const membershipTreeNames = (trees) => (trees || []).map((t) => {
  const name = t.name || t.id;
  return t.segment_id ? `${name} (${t.segment_id})` : name;
}).join(', ');

const membershipDiffText = (diff) => {
  const names = membershipTreeNames(diff.trees);
  if (diff.kind === MEMBERSHIP_CONFIG_ONLY) {
    return names
      ? `Segment ${diff.segment_id} is in the device config, but segment "${names}" does not list this device.`
      : `Segment ${diff.segment_id} is in the device config, but no registered segment owns it.`;
  }
  return `Segment "${names}" lists this device, but segment ${diff.segment_id} is missing from its config.`;
};

const membershipKeepDeviceDisabled = (diff) => diff.kind === MEMBERSHIP_CONFIG_ONLY
  && (diff.trees || []).length !== 1;

const membershipOptionLabels = (diff) => {
  const names = membershipTreeNames(diff.trees);
  if (diff.kind === MEMBERSHIP_CONFIG_ONLY) {
    return {
      device: names && !membershipKeepDeviceDisabled(diff)
        ? `Keep device config (add device to "${names}")`
        : 'Keep device config (no single segment owns this ID)',
      tree: `Keep custom tree (remove ${diff.segment_id} from config)`,
    };
  }
  return {
    device: `Keep device config (remove device from "${names}")`,
    tree: `Keep custom tree (add ${diff.segment_id} to config)`,
  };
};

const buildDefaultMembershipChoices = (devices) => {
  const m = {};
  (devices || []).forEach((d) => {
    if (!d?.serial) return;
    m[d.serial] = {
      membership: Object.fromEntries((d.membership_diffs || []).map((diff) => [
        String(diff.segment_id),
        MEMBERSHIP_KEEP_TREE,
      ])),
    };
  });
  return m;
};

const buildMembershipFixPayload = (serials, choices) => serials.map((serial) => ({
  serial,
  membership: Object.entries(choices[serial]?.membership || {})
    .filter(([, keep]) => keep !== MEMBERSHIP_SKIP)
    .map(([id, keep]) => ({ segment_id: Number(id), keep })),
}));

const AUDIT_FIX_ADD_DEV = 'add_dev';
const AUDIT_FIX_REMOVE_AP = 'remove_ap';
const AUDIT_FIX_ADD_AP = 'add_ap';
const AUDIT_FIX_REMOVE_DEV = 'remove_dev';

const buildDefaultAuditFixChoices = (devices) => {
  const m = {};
  (devices || []).forEach((d) => {
    if (!d?.serial) return;
    m[d.serial] = {
      apNotInDev: Object.fromEntries((d.ap_not_in_dev || []).map((id) => [String(id), AUDIT_FIX_ADD_DEV])),
      devNotInAp: Object.fromEntries((d.dev_not_in_ap || []).map((id) => [String(id), AUDIT_FIX_ADD_AP])),
    };
  });
  return m;
};

const buildAuditFixPayload = (serials, choices) => serials.map((serial) => {
  const c = choices[serial] || {};
  const fix = {
    serial,
    add_to_dev: [],
    add_to_ap: [],
    remove_from_dev: [],
    remove_from_ap: [],
  };
  Object.entries(c.apNotInDev || {}).forEach(([id, action]) => {
    const n = Number(id);
    if (action === AUDIT_FIX_REMOVE_AP) fix.remove_from_ap.push(n);
    else fix.add_to_dev.push(n);
  });
  Object.entries(c.devNotInAp || {}).forEach(([id, action]) => {
    const n = Number(id);
    if (action === AUDIT_FIX_REMOVE_DEV) fix.remove_from_dev.push(n);
    else fix.add_to_ap.push(n);
  });
  return fix;
});

const AUDIT_MODES = [
  {
    value: AUDIT_MODE_ORPHANS,
    label: 'Detect orphan segments',
    help: 'Unknown segment IDs and dev/ap mismatches (non-CAP). Choose per ID whether to add the missing side or remove the extra side.',
  },
  {
    value: AUDIT_MODE_AP_WILDCARDS,
    label: 'Detect "All segment devices"',
    help: 'Devices (including CAP) with 0xFEFE or 0xFFFF in segment_id_ap. Apply removes these AP wildcards only.',
  },
  {
    value: AUDIT_MODE_UNASSIGNED,
    label: 'Devices not in a segment',
    help: 'Lists site devices not registered in any NMS segment (custom tree). Add them to an existing segment.',
  },
  {
    value: AUDIT_MODE_MEMBERSHIP,
    label: 'Segment membership',
    help: 'Checks every device config against all registered segments (custom trees). For each mismatch, choose whether the device config or the custom tree is correct.',
  },
];

const SEGMENT_CATALOG = {
  id: 'segment',
  title: 'Segment',
  short: 'Partition the site into CAP-centric segments (best connectivity, depth-aware)',
  icon: HubIcon,
  color: 'primary',
  isMeta: false,
  tooltip:
    'Site master plan (all devices, online and offline): one segment per CAP — the CAP plus the lamps that connect to it best. Lamps with a solid direct CAP link (≥ -70 dBm) become hop-1 children first; weaker/multi-hop links then compete on affinity (15% penalty per hop). Apply starts a two-phase rollout (handles offline members) tracked under Plans → Segment Plans.',
  description: (
    <>
      CAP-centric site segmentation built from the golden neighbour graph.
      <ul style={{ margin: '4px 0 4px', paddingLeft: 18 }}>
        <li><strong>Master plan</strong> — segments all site devices, online and offline.</li>
        <li><strong>Seed</strong> — one segment per CAP.</li>
        <li><strong>Qualified direct children first</strong> — every lamp with a solid direct CAP link (≥ -70 dBm) is placed as that CAP&apos;s hop-1 child before any multi-hop path; it is never pulled into a deeper neighbouring segment.</li>
        <li><strong>Grow outward</strong> — weak direct links and all multi-hop paths then compete on affinity, so a strong 2-hop path can beat a marginal direct link.</li>
        <li><strong>Hop penalty</strong> — affinity multiplies by 0.85 (15%) per hop, biasing toward shallower / stronger paths.</li>
        <li><strong>Stability</strong> — optionally bias toward a lamp&apos;s current serving CAP (configurable, default off) so minor RF jitter doesn&apos;t reshuffle stable segments between runs (a materially better CAP still wins).</li>
        <li><strong>Advisories</strong> — a segment is flagged for review (it can still be applied) when a proposed parent would serve more children than its radios support (<em>over capacity</em>) or the chain is deeper than the configured max depth (default 4 hops; <em>deep chain</em>).</li>
        <li><strong>Unassigned</strong> — lamps no CAP can reach are listed separately; no segment is written for them.</li>
        <li><strong>Apply</strong> — each segment becomes a custom tree and starts a two-phase rollout that handles offline members: phase 1 seeds the segment + a 0xFEFE &quot;accept any child&quot; failsafe to every member (online now, offline on reconnect); phase 2 strips the failsafe once all are online. Track progress under <strong>Plans → Segment Plans</strong>.</li>
      </ul>
    </>
  ),
  algorithm: 'Master plan over all devices (online + offline). Qualified direct children (≥ -70 dBm) first, then weak/multi-hop paths ranked by affinity = link-quality × 0.85^hops',
  measures: 'Share of lamps assigned to a segment (higher is better).',
  actsOn: 'POST /tree-mgmt/custom-trees — creates one custom tree per applied segment (sets segment_id_dev / segment_id_ap).',
};

const shortSerial = (serial) => {
  const s = String(serial || '');
  return s.length > 8 ? `…${s.slice(-6)}` : s;
};

// DecisionBadges renders the v2 per-lamp reasoning (path headroom, whether the
// planner moved it, and whether it's a close call). Renders a dash when no v2
// decision is available (e.g. an older backend or v2 disabled).
const DecisionBadges = ({ decision }) => {
  if (!decision) return <Typography variant="caption" color="text.secondary">—</Typography>;
  const redundancy = decision.redundancy ?? 0;
  const bindingLabel = BINDING_CONSTRAINT_LABELS[decision.binding_constraint] || '';
  return (
    <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="wrap" sx={{ gap: 0.5 }}>
      <Tooltip title={`${HEADROOM_TOOLTIP} (best path cost ${decision.best_cost ?? '—'}, ${decision.alt_caps ?? 0} reachable CAP${decision.alt_caps === 1 ? '' : 's'}).`}>
        <Chip
          size="small"
          variant="outlined"
          color={redundancy === 0 ? 'warning' : 'default'}
          label={redundancy === 0 ? 'no backup' : `${redundancy} backup${redundancy === 1 ? '' : 's'}`}
        />
      </Tooltip>
      {decision.moved && bindingLabel && (
        <Tooltip title={MOVED_TOOLTIP}>
          <Chip size="small" color="info" variant="outlined" label={bindingLabel} />
        </Tooltip>
      )}
      {decision.confidence === 'marginal' && (
        <Tooltip title={MARGINAL_TOOLTIP}>
          <Chip size="small" color="warning" variant="outlined" label="marginal" />
        </Tooltip>
      )}
    </Stack>
  );
};

const SegmentPanel = ({ devices, isActive, initialCohort, onCohortApplied }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { refresh: refreshDataStatus } = useNeighborData();
  const { missingSerials, clearMissing, runGuarded, scanMissing, scanningSerials } = useAnalyserGate();

  const [loading, setLoading] = useState(false);
  // cohort: when set (from a map "Create segments for selection"), the
  // partitioning runs only on these serials. null = whole-site.
  const [cohort, setCohort] = useState(null);
  const [result, setResult] = useState(null);
  const [selectedSegmentKey, setSelectedSegmentKey] = useState(null);
  const [busySegmentKey, setBusySegmentKey] = useState(null);
  const [applyAllBusy, setApplyAllBusy] = useState(false);
  const [existingTrees, setExistingTrees] = useState([]);
  const existingSegmentBySerial = useMemo(() => buildExistingSegmentBySerial(existingTrees), [existingTrees]);
  const existingTreesByCap = useMemo(() => buildExistingTreesByCap(existingTrees), [existingTrees]);
  const [maxHopDepth, setMaxHopDepth] = useState(DEFAULT_MAX_HOP_DEPTH);
  const [minRssi, setMinRssi] = useState(DEFAULT_MIN_RSSI);
  const [hysteresisPct, setHysteresisPct] = useState(DEFAULT_HYSTERESIS_PCT);
  const [paramsDirty, setParamsDirty] = useState(false);
  // Manual overrides: unassigned serial -> chosen candidate option. These
  // are merged into the apply payload so a manually-assigned lamp is
  // written into the segment the operator picked.
  const [manualAssignments, setManualAssignments] = useState({});

  // Save / load saved proposals.
  const [savedProposals, setSavedProposals] = useState([]);
  const [loadMenuAnchor, setLoadMenuAnchor] = useState(null);
  const [loadingProposals, setLoadingProposals] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [partialConfirmOpen, setPartialConfirmOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [savingProposal, setSavingProposal] = useState(false);
  const [loadedProposalId, setLoadedProposalId] = useState(null);
  const [loadedProposalName, setLoadedProposalName] = useState('');
  const restoreAttemptedRef = useRef(false);

  // Segment audit (config scan + fix).
  const [auditResult, setAuditResult] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [applyingSerials, setApplyingSerials] = useState(() => new Set());
  const [auditMode, setAuditMode] = useState(AUDIT_MODE_ORPHANS);
  const [assignAllTreeId, setAssignAllTreeId] = useState('');
  const [assignTreeBySerial, setAssignTreeBySerial] = useState(() => ({}));
  const [auditFixChoices, setAuditFixChoices] = useState({});
  const [allowIncompleteNeighborsMode, setAllowIncompleteNeighborsMode] = useState(false);
  const [innerTab, setInnerTab] = useState('partitioning');

  // Connectivity lookup so we can flag offline members before apply.
  // Apply is no longer blocked by offline devices — the rollout writes
  // their config and applies it on reconnect — but it's useful context.
  const connectedBySerial = useMemo(() => {
    const m = new Map();
    (devices || []).forEach((d) => {
      if (d?.serial_number) m.set(normalizeSerial(d.serial_number), d.connected !== false);
    });
    return m;
  }, [devices]);

  const segments = useMemo(() => result?.segments || [], [result]);
  const unassigned = useMemo(() => result?.unassigned || [], [result]);
  const missingNeighborSerials = useMemo(
    () => [...(result?.missing_neighbor_serials || [])].sort(),
    [result],
  );
  const neighborDataPartial = !!(result?.neighbor_data_partial && missingNeighborSerials.length > 0);
  const unassignedList = useMemo(() => {
    const optsBySerial = new Map();
    (result?.unassigned_options || []).forEach((entry) => {
      if (entry?.serial) optsBySerial.set(normalizeSerial(entry.serial), entry);
    });
    const missingSet = new Set(missingNeighborSerials.map((s) => normalizeSerial(s)));
    const serials = (result?.unassigned || []).length
      ? result.unassigned
      : [...optsBySerial.keys()];
    return serials
      .filter((serial) => {
        if (!neighborDataPartial) return true;
        return !missingSet.has(normalizeSerial(serial));
      })
      .map((serial) => {
        const entry = optsBySerial.get(normalizeSerial(serial));
        return entry || { serial, options: [] };
      });
  }, [result, missingNeighborSerials, neighborDataPartial]);
  const assignedSegmentMemberSerials = useMemo(() => {
    const m = new Set();
    segments.forEach((seg) => {
      (seg.cap_serials || []).forEach((s) => m.add(normalizeSerial(s)));
      (seg.members || []).forEach((mem) => m.add(normalizeSerial(mem.serial)));
    });
    return m;
  }, [segments]);
  const missingNeighborManualList = useMemo(
    () => missingNeighborSerials.filter((s) => !assignedSegmentMemberSerials.has(normalizeSerial(s))),
    [missingNeighborSerials, assignedSegmentMemberSerials],
  );
  const pendingMissingNeighborAssignments = useMemo(
    () => missingNeighborManualList.filter((s) => !manualAssignments[s]?.segment_key),
    [missingNeighborManualList, manualAssignments],
  );
  const applyBlockedByManualAssignments = neighborDataPartial && pendingMissingNeighborAssignments.length > 0;
  const summary = result?.summary;

  // --- v2 (capacity + interference) lookups ------------------------------
  const decisionsBySerial = useMemo(() => {
    const m = new Map();
    (result?.decisions || []).forEach((d) => {
      if (d?.serial) m.set(normalizeSerial(d.serial), d);
    });
    return m;
  }, [result]);
  const capacitySummary = result?.capacity_summary || null;
  const interference = result?.interference || null;
  const ceilingByCap = useMemo(() => {
    const m = new Map();
    (result?.segment_ceilings || []).forEach((c) => {
      if (c?.cap) m.set(normalizeSerial(c.cap), c);
    });
    return m;
  }, [result]);
  const hasV2 = !!(capacitySummary || interference || decisionsBySerial.size > 0);

  // segment key -> [serials manually added to it] (pending apply).
  const manualAddsByKey = useMemo(() => {
    const m = {};
    Object.entries(manualAssignments).forEach(([serial, opt]) => {
      if (!opt?.segment_key) return;
      (m[opt.segment_key] = m[opt.segment_key] || []).push(serial);
    });
    return m;
  }, [manualAssignments]);

  // segment key -> diff against the CAP's registered segment.
  const segmentDiffByKey = useMemo(() => {
    const m = new Map();
    segments.forEach((seg) => {
      m.set(seg.key, diffProposedSegment(seg, manualAddsByKey[seg.key], existingTreesByCap));
    });
    return m;
  }, [segments, manualAddsByKey, existingTreesByCap]);
  const changedSegments = useMemo(
    () => segments.filter((seg) => !segmentDiffByKey.get(seg.key)?.unchanged),
    [segments, segmentDiffByKey],
  );

  const assignUnassigned = useCallback((serial, option) => {
    setManualAssignments((prev) => ({ ...prev, [serial]: option }));
    enqueueSnackbar(`${shortSerial(serial)} will be added to segment ${shortSerial(option.cap_serial)} on apply.`, { variant: 'info' });
  }, [enqueueSnackbar]);

  const clearUnassigned = useCallback((serial) => {
    setManualAssignments((prev) => {
      const next = { ...prev };
      delete next[serial];
      return next;
    });
  }, []);

  const assignMissingToSegment = useCallback((serial, segmentKey) => {
    const seg = segments.find((s) => s.key === segmentKey);
    if (!seg) return;
    const cap = seg.cap_serials?.[0] || seg.key;
    assignUnassigned(serial, {
      segment_key: seg.key,
      cap_serial: cap,
      parent_serial: cap,
      hop_depth: null,
      no_neighbor_data: true,
    });
  }, [segments, assignUnassigned]);

  const segmentMapDevices = useMemo(
    () => (devices || []).filter(isJnet2CapOrNode),
    [devices],
  );

  const segmentMapHasGPS = useMemo(
    () => segmentMapDevices.some((d) => d?.gps || d?.gps_location || d?.gps_override),
    [segmentMapDevices],
  );

  const segmentStats = useCallback((seg) => {
    const memberSerials = (seg.members || []).map((m) => m.serial);
    const allSerials = [...(seg.cap_serials || []), ...memberSerials];
    let offline = 0;
    allSerials.forEach((s) => {
      if (connectedBySerial.get(normalizeSerial(s)) === false) offline += 1;
    });
    const targetTree = segmentDiffByKey.get(seg.key)?.tree || null;
    const otherSegments = memberSerials
      .map((s) => otherExistingSegment(existingSegmentBySerial, s, targetTree))
      .filter(Boolean);
    const conflicts = otherSegments.length;
    return { offline, conflicts, otherSegments, memberCount: memberSerials.length };
  }, [connectedBySerial, existingSegmentBySerial, segmentDiffByKey]);

  // Adopt a map-selected cohort when navigated here from the device map.
  useEffect(() => {
    if (Array.isArray(initialCohort) && initialCohort.length > 0) {
      setCohort(initialCohort);
      setParamsDirty(true);
      enqueueSnackbar(
        `Segment partitioning scoped to ${initialCohort.length} selected device${initialCohort.length === 1 ? '' : 's'}. Press Run to partition this cohort.`,
        { variant: 'info' },
      );
      onCohortApplied?.();
    }
  }, [initialCohort, onCohortApplied, enqueueSnackbar]);

  const clearCohort = useCallback(() => {
    setCohort(null);
    setParamsDirty(true);
  }, []);

  const clearLoadedProposal = useCallback(() => {
    setLoadedProposalId(null);
    setLoadedProposalName('');
    clearLoadedSegmentProposal();
  }, []);

  const refreshExistingSegments = useCallback(async () => {
    try {
      const trees = await api.getCustomTrees();
      setExistingTrees(trees);
    } catch {
      setExistingTrees([]);
    }
  }, []);

  // A loaded plan is compared against the current segments, so refresh
  // them whenever a result is shown.
  useEffect(() => {
    if (result) refreshExistingSegments();
  }, [result, refreshExistingSegments]);

  const applyProposalDoc = useCallback((doc, { silent = false } = {}) => {
    const payload = doc?.payload || {};
    if (payload.result) setResult(payload.result);
    setManualAssignments(payload.overrides || {});
    setAllowIncompleteNeighborsMode(!!payload.allow_incomplete_neighbors);
    if (doc?.params) {
      if (doc.params.max_hop_depth) setMaxHopDepth(doc.params.max_hop_depth);
      if (doc.params.min_rssi_dbm) setMinRssi(doc.params.min_rssi_dbm);
      setHysteresisPct(Math.round((doc.params.hysteresis_bonus || 0) * 100));
    }
    setSelectedSegmentKey(null);
    setParamsDirty(false);
    const id = doc?.id;
    const name = doc?.name || id;
    if (id) {
      setLoadedProposalId(id);
      setLoadedProposalName(name || id);
      persistLoadedSegmentProposal({ id, name });
    }
    if (!silent) {
      enqueueSnackbar(`Loaded plan "${name || id}".`, { variant: 'info' });
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    if (restoreAttemptedRef.current) return undefined;
    restoreAttemptedRef.current = true;
    const stored = readLoadedSegmentProposal();
    if (!stored?.id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const doc = await api.getSegmentProposal(stored.id);
        if (cancelled) return;
        applyProposalDoc(doc, { silent: true });
      } catch {
        if (!cancelled) clearLoadedProposal();
      }
    })();
    return () => { cancelled = true; };
  }, [applyProposalDoc, clearLoadedProposal]);

  const runAnalysis = useCallback(async ({ allowIncompleteNeighbors } = {}) => {
    const usePartial = allowIncompleteNeighbors ?? allowIncompleteNeighborsMode;
    setLoading(true);
    setSelectedSegmentKey(null);
    // Best-effort: learn which serials are already in a custom tree so we
    // can warn before overwriting their segment assignment.
    try {
      const trees = await api.getCustomTrees();
      setExistingTrees(trees);
    } catch {
      setExistingTrees([]);
    }

    const outcome = await runGuarded(() => api.getSiteSegmentation({
      maxHopDepth,
      minRssi,
      hysteresisBonus: hysteresisPct / 100,
      cohort: cohort || undefined,
      allowIncompleteNeighbors: usePartial,
    }));
    setLoading(false);
    if (outcome.ok) {
      clearMissing();
      clearLoadedProposal();
      setResult(outcome.data);
      setManualAssignments({});
      setParamsDirty(false);
      const partial = !!outcome.data?.neighbor_data_partial;
      setAllowIncompleteNeighborsMode(partial);
      const s = outcome.data?.summary;
      const missingCount = (outcome.data?.missing_neighbor_serials || []).length;
      enqueueSnackbar(
        partial
          ? `Segmentation (partial neighbour data): ${s?.segment_count ?? 0} segment(s), ${s?.assigned_count ?? 0} assigned`
            + ` — ${missingCount} device${missingCount === 1 ? '' : 's'} excluded from RF planning; assign manually below.`
          : `Segmentation: ${s?.segment_count ?? 0} segment(s), ${s?.assigned_count ?? 0} lamp(s) assigned`
            + (s?.unassigned_count ? `, ${s.unassigned_count} unassigned.` : '.'),
        { variant: partial ? 'warning' : 'info' },
      );
      return;
    }
    if (outcome.gated) {
      setResult(null);
      if (!usePartial) {
        setAllowIncompleteNeighborsMode(false);
        enqueueSnackbar('Segmentation cannot run: neighbour data is incomplete.', { variant: 'error' });
      }
      return;
    }
    setAllowIncompleteNeighborsMode(false);
    setResult(null);
    if (outcome.error?.statusCode === 409) {
      enqueueSnackbar(
        outcome.error?.message || 'A segmentation analysis is already running for this site. Please wait for it to finish.',
        { variant: 'warning' },
      );
      return;
    }
    if (outcome.error?.data?.error_code === 'cohort_no_cap') {
      enqueueSnackbar(
        outcome.error?.data?.error_data?.message || 'The selected devices include no CAP. Add a CAP to the selection and re-run.',
        { variant: 'warning' },
      );
      return;
    }
    enqueueSnackbar(outcome.error?.message || 'Failed to run segmentation', { variant: 'error' });
  }, [enqueueSnackbar, runGuarded, clearMissing, clearLoadedProposal, maxHopDepth, minRssi, hysteresisPct, cohort, allowIncompleteNeighborsMode]);

  const runAnalysisFromHeader = useCallback(
    () => runAnalysis(),
    [runAnalysis],
  );

  const confirmPartialRun = useCallback(async () => {
    setPartialConfirmOpen(false);
    await runAnalysis({ allowIncompleteNeighbors: true });
  }, [runAnalysis]);

  const handleScanMissing = useCallback(async () => {
    const s = await scanMissing();
    if (!s) return;
    enqueueSnackbar(
      s.failed === 0
        ? `Triggered neighbour scan for ${s.accepted} device(s).`
        : `Triggered ${s.accepted} scan(s); ${s.failed} failed to start.`,
      { variant: s.failed === 0 ? 'info' : 'warning' },
    );
    refreshDataStatus();
  }, [scanMissing, enqueueSnackbar, refreshDataStatus]);

  // Applies one proposed segment. When the CAP already has a registered
  // segment, only the new lamps are added to it (keeping its segment ID);
  // otherwise a new segment is created. Unchanged segments are skipped.
  const applySegment = useCallback(async (seg) => {
    const diff = segmentDiffByKey.get(seg.key)
      || diffProposedSegment(seg, manualAddsByKey[seg.key], existingTreesByCap);
    if (diff.unchanged) {
      return { skipped: true, added: 0 };
    }
    if (diff.tree) {
      if (diff.tree.status === 'provisioning') {
        throw new Error(`Segment "${diff.tree.name || diff.tree.id}" is still rolling out. Wait for its plan to finish.`);
      }
      const resp = await api.addDevicesToCustomTree(diff.tree.id, { devices: diff.toAdd, move: true });
      return { ...resp, added: diff.toAdd.length };
    }
    const cap = seg.cap_serials?.[0];
    const resp = await api.createCustomTree({
      name: `Segment ${shortSerial(cap)}`,
      caps: seg.cap_serials || [],
      devices: diff.toAdd,
      move: true,
    });
    return { ...resp, added: diff.toAdd.length };
  }, [segmentDiffByKey, manualAddsByKey, existingTreesByCap]);

  const handleApplyOne = useCallback(async (seg) => {
    if (applyBlockedByManualAssignments) {
      enqueueSnackbar(
        `Assign ${pendingMissingNeighborAssignments.length} excluded device(s) to a segment before applying.`,
        { variant: 'warning' },
      );
      return;
    }
    setBusySegmentKey(seg.key);
    try {
      const resp = await applySegment(seg);
      if (resp?.skipped) {
        enqueueSnackbar(`Segment ${shortSerial(seg.cap_serials?.[0])} already matches the plan. Nothing to apply.`, { variant: 'info' });
        return;
      }
      const offline = (resp?.offline_devices || []).length;
      const base = `Rollout started for segment ${shortSerial(seg.cap_serials?.[0])} (${resp?.added ?? 0} lamp(s) to add).`;
      enqueueSnackbar(
        offline > 0
          ? `${base} ${offline} offline — will apply on reconnect. Track under Plans → Segment Plans.`
          : `${base} Track progress under Plans → Segment Plans.`,
        { variant: 'success' },
      );
      await (loadedProposalId ? refreshExistingSegments() : runAnalysis());
    } catch (error) {
      const msg = error?.statusCode === 409
        ? 'Some members are already in an in-flight segment plan. See Plans → Segment Plans.'
        : (error?.message || 'Failed to start segment rollout');
      enqueueSnackbar(msg, { variant: error?.statusCode === 409 ? 'warning' : 'error' });
    } finally {
      setBusySegmentKey(null);
    }
  }, [applySegment, enqueueSnackbar, runAnalysis, refreshExistingSegments, loadedProposalId, applyBlockedByManualAssignments, pendingMissingNeighborAssignments.length]);

  const handleApplyAll = useCallback(async () => {
    if (!changedSegments.length) return;
    if (applyBlockedByManualAssignments) {
      enqueueSnackbar(
        `Assign ${pendingMissingNeighborAssignments.length} excluded device(s) to a segment before applying.`,
        { variant: 'warning' },
      );
      return;
    }
    setApplyAllBusy(true);
    let applied = 0;
    let offlineTotal = 0;
    let hadError = false;
    try {
      for (const seg of changedSegments) {
        // eslint-disable-next-line no-await-in-loop
        const resp = await applySegment(seg);
        if (resp?.skipped) continue;
        offlineTotal += (resp?.offline_devices || []).length;
        applied += 1;
      }
      enqueueSnackbar(
        offlineTotal > 0
          ? `Started ${applied} rollout(s); ${offlineTotal} offline member(s) will apply on reconnect. Track under Plans → Segment Plans.`
          : `Started ${applied} rollout(s). Track under Plans → Segment Plans.`,
        { variant: 'success' },
      );
    } catch (error) {
      hadError = true;
      enqueueSnackbar(`${applied} started; stopped on error: ${error?.message || 'unknown'}`, { variant: 'error' });
    } finally {
      setApplyAllBusy(false);
      if (applied > 0 || hadError) {
        await (loadedProposalId ? refreshExistingSegments() : runAnalysis());
      }
    }
  }, [changedSegments, applySegment, enqueueSnackbar, runAnalysis, refreshExistingSegments, loadedProposalId, applyBlockedByManualAssignments, pendingMissingNeighborAssignments.length]);

  // --- Save / load proposals ---------------------------------------------

  const handleSaveProposal = useCallback(async () => {
    const name = saveName.trim();
    if (!name || !result) return;
    setSavingProposal(true);
    try {
      await api.saveSegmentProposal({
        name,
        params: {
          max_hop_depth: maxHopDepth,
          min_rssi_dbm: minRssi,
          hysteresis_bonus: hysteresisPct / 100,
        },
        segment_count: result?.summary?.segment_count ?? (result?.segments || []).length,
        assigned_count: result?.summary?.assigned_count ?? 0,
        unassigned_count: result?.summary?.unassigned_count ?? 0,
        payload: {
          result,
          overrides: manualAssignments,
          allow_incomplete_neighbors: neighborDataPartial,
        },
      });
      enqueueSnackbar(`Saved plan "${name}".`, { variant: 'success' });
      setSaveDialogOpen(false);
      setSaveName('');
    } catch (error) {
      enqueueSnackbar(error?.message || 'Failed to save plan', { variant: 'error' });
    } finally {
      setSavingProposal(false);
    }
  }, [saveName, result, maxHopDepth, minRssi, hysteresisPct, manualAssignments, neighborDataPartial, enqueueSnackbar]);

  const openLoadMenu = useCallback(async (e) => {
    setLoadMenuAnchor(e.currentTarget);
    setLoadingProposals(true);
    try {
      const list = await api.listSegmentProposals();
      setSavedProposals(Array.isArray(list) ? list : []);
    } catch (error) {
      enqueueSnackbar(error?.message || 'Failed to list saved plans', { variant: 'error' });
      setSavedProposals([]);
    } finally {
      setLoadingProposals(false);
    }
  }, [enqueueSnackbar]);

  const handleLoadProposal = useCallback(async (id) => {
    setLoadMenuAnchor(null);
    try {
      const doc = await api.getSegmentProposal(id);
      applyProposalDoc(doc);
    } catch (error) {
      enqueueSnackbar(error?.message || 'Failed to load plan', { variant: 'error' });
    }
  }, [applyProposalDoc, enqueueSnackbar]);

  const handleDeleteProposal = useCallback(async (id, name) => {
    try {
      await api.deleteSegmentProposal(id);
      setSavedProposals((prev) => prev.filter((p) => p.id !== id));
      if (id === loadedProposalId) {
        clearLoadedProposal();
      }
      enqueueSnackbar(`Deleted plan "${name || id}".`, { variant: 'info' });
    } catch (error) {
      enqueueSnackbar(error?.message || 'Failed to delete plan', { variant: 'error' });
    }
  }, [enqueueSnackbar, loadedProposalId, clearLoadedProposal]);

  // --- Segment audit ------------------------------------------------------

  const registeredSegments = useMemo(
    () => auditResult?.registered_segments || [],
    [auditResult],
  );

  const auditSuccessMessage = useCallback((count) => {
    if (auditMode === AUDIT_MODE_UNASSIGNED) {
      return count === 0
        ? 'All site devices belong to a registered segment.'
        : `${count} device(s) not assigned to any segment.`;
    }
    if (auditMode === AUDIT_MODE_MEMBERSHIP) {
      return count === 0
        ? 'Segment membership: every device config matches its segments.'
        : `Segment membership: ${count} device(s) do not match their segments.`;
    }
    return count === 0
      ? 'Segment audit: no configuration issues found.'
      : `Segment audit: ${count} device(s) with a segment config issue.`;
  }, [auditMode]);

  const runAudit = useCallback(async () => {
    setAuditLoading(true);
    try {
      const data = await api.getSegmentAudit({ mode: auditMode });
      setAuditResult(data);
      setAssignTreeBySerial({});
      if (auditMode === AUDIT_MODE_ORPHANS) {
        setAuditFixChoices(buildDefaultAuditFixChoices(data?.devices));
      } else if (auditMode === AUDIT_MODE_MEMBERSHIP) {
        setAuditFixChoices(buildDefaultMembershipChoices(data?.devices));
      } else {
        setAuditFixChoices({});
      }
      const n = (data?.devices || []).length;
      enqueueSnackbar(
        auditSuccessMessage(n),
        { variant: n === 0 ? 'success' : 'warning' },
      );
    } catch (error) {
      enqueueSnackbar(error?.message || 'Failed to run segment audit', { variant: 'error' });
    } finally {
      setAuditLoading(false);
    }
  }, [auditMode, auditSuccessMessage, enqueueSnackbar]);

  const applyAuditSerials = useCallback(async (serials) => {
    if (!serials.length) return;
    setApplyingSerials((prev) => {
      const next = new Set(prev);
      serials.forEach((s) => next.add(s));
      return next;
    });
    try {
      let fixes;
      if (auditMode === AUDIT_MODE_ORPHANS) {
        fixes = buildAuditFixPayload(serials, auditFixChoices);
      } else if (auditMode === AUDIT_MODE_MEMBERSHIP) {
        fixes = buildMembershipFixPayload(serials, auditFixChoices);
      }
      const resp = await api.applySegmentAudit(serials, { mode: auditMode, fixes });
      const results = resp?.results || [];
      const fixed = results.filter((r) => r.fixed).length;
      const failed = results.filter((r) => r.error).length;
      enqueueSnackbar(
        failed === 0
          ? `Fixed ${fixed} device(s).`
          : `Fixed ${fixed}; ${failed} failed.`,
        { variant: failed === 0 ? 'success' : 'warning' },
      );
      await runAudit();
    } catch (error) {
      enqueueSnackbar(error?.message || 'Failed to apply segment fixes', { variant: 'error' });
    } finally {
      setApplyingSerials((prev) => {
        const next = new Set(prev);
        serials.forEach((s) => next.delete(s));
        return next;
      });
    }
  }, [auditMode, auditFixChoices, enqueueSnackbar, runAudit]);

  const assignToSegment = useCallback(async (serials, customTreeId) => {
    if (!serials.length) return;
    if (!customTreeId) {
      enqueueSnackbar('Select a segment first.', { variant: 'warning' });
      return;
    }
    setApplyingSerials((prev) => {
      const next = new Set(prev);
      serials.forEach((s) => next.add(s));
      return next;
    });
    try {
      const resp = await api.applySegmentAudit(serials, {
        mode: AUDIT_MODE_UNASSIGNED,
        customTreeId,
      });
      const results = resp?.results || [];
      const assigned = results.filter((r) => r.assigned).length;
      const failed = results.filter((r) => r.error).length;
      const treeName = results.find((r) => r.assigned_to_tree_name)?.assigned_to_tree_name;
      enqueueSnackbar(
        failed === 0
          ? `Added ${assigned} device(s) to ${treeName || 'segment'}.`
          : `Added ${assigned}; ${failed} failed.`,
        { variant: failed === 0 ? 'success' : 'warning' },
      );
      await runAudit();
    } catch (error) {
      enqueueSnackbar(error?.message || 'Failed to add devices to segment', { variant: 'error' });
    } finally {
      setApplyingSerials((prev) => {
        const next = new Set(prev);
        serials.forEach((s) => next.delete(s));
        return next;
      });
    }
  }, [enqueueSnackbar, runAudit]);

  if (!isActive) return null;

  return (
    <Box>
      <Tabs
        value={innerTab}
        onChange={(_e, v) => setInnerTab(v)}
        sx={{ minHeight: 40, mb: 1 }}
      >
        <Tab
          value="partitioning"
          icon={<HubIcon fontSize="small" />}
          iconPosition="start"
          label="Partitioning"
          sx={{ minHeight: 40, textTransform: 'none' }}
        />
        <Tab
          value="audit"
          icon={<CleaningServicesIcon fontSize="small" />}
          iconPosition="start"
          label="Audit"
          sx={{ minHeight: 40, textTransform: 'none' }}
        />
      </Tabs>

      <Box sx={{ display: innerTab === 'partitioning' ? 'block' : 'none' }}>
      <AnalyzerHeader
        catalog={SEGMENT_CATALOG}
        loading={loading}
        onRun={runAnalysisFromHeader}
        hasResult={!!result}
        rightContent={<NeighborDataChipConnected />}
      />

      {cohort?.length > 0 && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={<Button color="inherit" size="small" onClick={clearCohort}>Use whole site</Button>}
        >
          Scoped to a map selection of {cohort.length} device{cohort.length === 1 ? '' : 's'}.
          Partitioning runs only on this cohort — in-cohort CAPs are segment roots.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
        <Accordion
          disableGutters
          elevation={0}
          defaultExpanded={false}
          sx={{
            '&:before': { display: 'none' },
            bgcolor: 'transparent',
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 1.5, minHeight: 44 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Options
              </Typography>
              <Typography variant="caption" color="text.secondary">
                depth {maxHopDepth} · min RSSI {minRssi} dBm · stability {hysteresisPct}%
              </Typography>
              {paramsDirty && result && (
                <Chip size="small" color="warning" variant="outlined" label="re-run to apply" />
              )}
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.5 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-end' }}>
              <Tooltip title={MAX_HOP_DEPTH_TOOLTIP}>
                <Box sx={{ width: { xs: '100%', sm: 220 } }}>
                  <NumericParamField
                    label="Max hop depth"
                    size="small"
                    fullWidth
                    value={maxHopDepth}
                    fallback={DEFAULT_MAX_HOP_DEPTH}
                    min={MIN_MAX_HOP_DEPTH}
                    max={MAX_MAX_HOP_DEPTH}
                    parse={(v) => Math.round(v)}
                    onChange={(v) => {
                      setMaxHopDepth(v);
                      setParamsDirty(true);
                    }}
                    inputProps={{ step: 1 }}
                  />
                </Box>
              </Tooltip>
              <Tooltip title={MIN_RSSI_TOOLTIP}>
                <Box sx={{ width: { xs: '100%', sm: 220 } }}>
                  <NumericParamField
                    label="Min link RSSI (dBm)"
                    size="small"
                    fullWidth
                    value={minRssi}
                    fallback={DEFAULT_MIN_RSSI}
                    min={MIN_MIN_RSSI}
                    max={MAX_MIN_RSSI}
                    parse={(v) => Math.round(v)}
                    onChange={(v) => {
                      setMinRssi(v);
                      setParamsDirty(true);
                    }}
                    inputProps={{ step: 1 }}
                  />
                </Box>
              </Tooltip>
              <Tooltip title={HYSTERESIS_TOOLTIP}>
                <Box sx={{ width: { xs: '100%', sm: 220 } }}>
                  <NumericParamField
                    label="Stability bonus (%)"
                    size="small"
                    fullWidth
                    value={hysteresisPct}
                    fallback={DEFAULT_HYSTERESIS_PCT}
                    min={MIN_HYSTERESIS_PCT}
                    max={MAX_HYSTERESIS_PCT}
                    parse={(v) => Math.round(v)}
                    onChange={(v) => {
                      setHysteresisPct(v);
                      setParamsDirty(true);
                    }}
                    inputProps={{ step: 5 }}
                  />
                </Box>
              </Tooltip>
              <Typography variant="caption" color="text.secondary" sx={{ flex: 1, pb: { sm: 0.5 } }}>
                Max hop depth is a hard cap on how deep a chain may grow; the min link RSSI gates which links can join a segment
                (lamps beyond the cap or below the gate fall to <em>unassigned</em>, where you can still assign them manually).
                The stability bonus biases toward each lamp&apos;s current CAP (0 = off).
                {paramsDirty && result && ' Re-run to apply the new settings.'}
              </Typography>
            </Stack>
          </AccordionDetails>
        </Accordion>
        <Divider />
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ gap: 1, p: 1.5 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<SaveIcon />}
            disabled={!result}
            onClick={() => { setSaveName(''); setSaveDialogOpen(true); }}
          >
            Save plan
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<FolderOpenIcon />}
            onClick={openLoadMenu}
          >
            Load plan
          </Button>
          {loadedProposalName && (
            <Chip
              size="small"
              color="info"
              variant="outlined"
              label={`Loaded: ${loadedProposalName}`}
            />
          )}
          <Typography variant="caption" color="text.secondary">
            Persist the current proposal (segments, manual assignments and settings) to reload later before applying.
          </Typography>
        </Stack>
      </Paper>

      {missingSerials.length > 0 && (
        <NeighborDataIncompleteCard
          missingSerials={missingSerials}
          onScanMissing={handleScanMissing}
          scanningSerials={scanningSerials}
          onContinueWithoutNeighbors={() => setPartialConfirmOpen(true)}
          continueHelp="Use only when you cannot scan now. Devices without a golden neighbour report are excluded from RF-based segmentation — you must assign them manually, and the proposed segments may not reflect true device relationships."
        />
      )}

      {result && neighborDataPartial && applyBlockedByManualAssignments && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Manual assignment required before apply</AlertTitle>
          <Typography variant="body2">
            {pendingMissingNeighborAssignments.length} device{pendingMissingNeighborAssignments.length === 1 ? '' : 's'} excluded
            from RF planning still need a segment — use the table below, then apply.
          </Typography>
        </Alert>
      )}

      {result && neighborDataPartial && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Partial neighbour data — segments may be inaccurate</AlertTitle>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Segmentation ran without golden neighbour reports for {missingNeighborSerials.length} connected
            Node{missingNeighborSerials.length === 1 ? '' : 's'}. Those devices were <strong>not</strong> used
            in RF path planning — the auto-assigned segments below may paint a wrong picture of how devices
            actually relate. Assign each excluded device to a segment manually before applying, or run a
            neighbour scan and re-run with complete data.
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5 }}>
            {missingNeighborSerials.map((serial) => (
              <Chip
                key={serial}
                size="small"
                variant="outlined"
                color="warning"
                label={serial}
                sx={{ fontFamily: 'monospace' }}
              />
            ))}
          </Stack>
        </Alert>
      )}

      {/* Map */}
      {result && segmentMapHasGPS && (
        <Paper sx={{ p: 1, mb: 2 }}>
          <SegmentMapView
            devices={segmentMapDevices}
            segments={segments}
            unassigned={unassigned}
            selectedSegmentKey={selectedSegmentKey}
            onSelectSegment={setSelectedSegmentKey}
            height={520}
          />
        </Paper>
      )}

      {result && !segmentMapHasGPS && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Map unavailable — {segmentMapDevices.length} jnet2 devices loaded but none have GPS coordinates.
          Segments are shown in the list below.
        </Alert>
      )}

      {/* Score + summary */}
      {result && (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <ScoreCard score={result.score} />
          </Box>
          <Paper variant="outlined" sx={{ flex: 1, p: 1.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Segmentation snapshot
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ gap: 0.75 }}>
              <Tooltip title="Segmentation reads the golden neighbor snapshot (same source as RRM optimize), not the live Neighbors tab. Re-mark neighbors golden after a site scan if the live report differs.">
                <Chip size="small" variant="outlined" label="uses golden neighbors" />
              </Tooltip>
              {neighborDataPartial && (
                <Tooltip title="Some connected devices had no golden neighbour report and were excluded from RF planning. Manual assignment is required before apply.">
                  <Chip
                    size="small"
                    color="warning"
                    variant="filled"
                    label={`${missingNeighborSerials.length} excluded from RF`}
                  />
                </Tooltip>
              )}
              <Chip size="small" label={`${summary?.cap_count ?? 0} CAPs`} />
              <Chip size="small" color="primary" label={`${summary?.segment_count ?? 0} segments`} />
              <Chip
                size="small"
                color={summary?.assigned_count > 0 ? 'success' : 'default'}
                label={`${summary?.assigned_count ?? 0} assigned`}
              />
              <Chip
                size="small"
                color={summary?.unassigned_count > 0 ? 'warning' : 'default'}
                variant={summary?.unassigned_count > 0 ? 'filled' : 'outlined'}
                label={`${summary?.unassigned_count ?? 0} unassigned`}
              />
              {summary?.max_hop_depth > 0 && (
                <Chip size="small" variant="outlined" label={`max depth ${summary.max_hop_depth} hops`} />
              )}
              {summary?.avg_hop_depth > 0 && (
                <Chip size="small" variant="outlined" label={`avg depth ${summary.avg_hop_depth}`} />
              )}
              {summary?.advisory_count > 0 && (
                <Tooltip title={`Segments flagged for review (still applicable). Over-capacity: a proposed parent would serve more children than its radios support. Deep chain: more than ${maxHopDepth} hops from the CAP, where latency/throughput degrade. Expand a flagged segment for details.`}>
                  <Chip
                    size="small"
                    color="warning"
                    label={`${summary.advisory_count} with advisories`}
                  />
                </Tooltip>
              )}
            </Stack>
          </Paper>
        </Stack>
      )}

      {/* v2: capacity + interference reasoning */}
      {result && hasV2 && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Capacity &amp; interference
            </Typography>
            <Tooltip title="Outputs of the v2 planner: a capacity-aware rebalance (keeps a slot reserve free per radio so optimize can re-parent) plus an interference-aware global search (nudges lamps off spectrally crowded CAPs when path cost is close). These shape the proposed segments below.">
              <Chip size="small" variant="outlined" label="v2" color="primary" />
            </Tooltip>
          </Stack>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ gap: 0.75 }}>
            {capacitySummary && (
              <>
                <Tooltip title="Radios serving more lamps than their usable slot budget (max_children minus the reserved maneuver/safety slots), after the rebalance vs. before. Goal is 0 after.">
                  <Chip
                    size="small"
                    color={capacitySummary.over_capacity_after > 0 ? 'warning' : 'success'}
                    variant={capacitySummary.over_capacity_after > 0 ? 'filled' : 'outlined'}
                    label={`over-capacity ${capacitySummary.over_capacity_after ?? 0}/${capacitySummary.over_capacity_before ?? 0}`}
                  />
                </Tooltip>
                <Tooltip title="Lamps the v2 planner relocated off the greedy seed to respect capacity or improve headroom/interference.">
                  <Chip size="small" variant="outlined" label={`${capacitySummary.leaf_moves ?? 0} moved`} />
                </Tooltip>
                <Tooltip title="Lamps with no backup path to their CAP (single point of failure) — optimize cannot re-parent them if the link degrades.">
                  <Chip
                    size="small"
                    color={capacitySummary.fragile_nodes > 0 ? 'warning' : 'default'}
                    variant="outlined"
                    label={`${capacitySummary.fragile_nodes ?? 0} fragile`}
                  />
                </Tooltip>
                <Tooltip title={MARGINAL_TOOLTIP}>
                  <Chip size="small" variant="outlined" label={`${capacitySummary.marginal_nodes ?? 0} marginal`} />
                </Tooltip>
              </>
            )}
            {interference && (
              <>
                <Tooltip title="CAPs whose serving channel the ACS survey reports as spectrally crowded (high busy % / foreign BSS count). The global search avoids piling more lamps onto these when alternatives are close.">
                  <Chip
                    size="small"
                    color={interference.crowded_caps > 0 ? 'warning' : 'default'}
                    variant="outlined"
                    label={`${interference.crowded_caps ?? 0} crowded CAPs`}
                  />
                </Tooltip>
                <Tooltip title="Lamps that hear two CAPs almost equally well — they sit on a segment seam where the other segment's co-channel energy bleeds in. Diagnostic; useful for the channel planner.">
                  <Chip size="small" variant="outlined" label={`${interference.boundary_nodes ?? 0} boundary`} />
                </Tooltip>
                <Tooltip title="How many CAPs had a fresh ACS survey backing the crowding signal. Lower coverage = crowding is based on partial data.">
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`ACS ${interference.acs_caps_covered ?? 0}/${interference.total_caps ?? 0}`}
                  />
                </Tooltip>
              </>
            )}
          </Stack>
        </Paper>
      )}

      {/* Segment list */}
      {result && segments.length > 0 && (
        <Paper sx={{ mb: 2 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider' }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Proposed segments ({segments.length})
            </Typography>
            <Tooltip title={applyBlockedByManualAssignments
              ? `Assign ${pendingMissingNeighborAssignments.length} excluded device(s) in the manual assignment table first.`
              : ''}
            >
              <span>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleApplyAll}
                  disabled={applyAllBusy || busySegmentKey != null || applyBlockedByManualAssignments || changedSegments.length === 0}
                  startIcon={applyAllBusy ? <CircularProgress size={16} color="inherit" /> : null}
                >
                  Apply all changes ({changedSegments.length})
                </Button>
              </span>
            </Tooltip>
          </Stack>
          {changedSegments.length === 0 && (
            <Alert severity="success" sx={{ m: 1.5 }}>
              No improvement: the current segments already match this plan.
            </Alert>
          )}

          {segments.map((seg, idx) => {
            const stats = segmentStats(seg);
            const color = segmentColorForIndex(idx);
            const isSelected = selectedSegmentKey === seg.key;
            const ceiling = ceilingByCap.get(normalizeSerial(seg.key || seg.cap_serials?.[0]));
            const diff = segmentDiffByKey.get(seg.key);
            const currentTreeName = diff?.tree ? (diff.tree.name || diff.tree.id) : '';
            return (
              <Accordion
                key={seg.key}
                expanded={isSelected}
                onChange={() => setSelectedSegmentKey(isSelected ? null : seg.key)}
                disableGutters
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ flexGrow: 1, flexWrap: 'wrap', gap: 0.5 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                      {seg.cap_serials?.[0]}
                    </Typography>
                    <Chip size="small" label={`${stats.memberCount} lamps`} />
                    {diff?.unchanged && (
                      <Tooltip title={`Matches the registered segment "${currentTreeName}". Nothing to apply.`}>
                        <Chip size="small" color="success" variant="outlined" label="No change" />
                      </Tooltip>
                    )}
                    {diff && !diff.unchanged && diff.tree && (
                      <Tooltip title={`Apply adds ${diff.toAdd.join(', ')} to the registered segment "${currentTreeName}" and keeps its segment ID.`}>
                        <Chip size="small" color="info" variant="outlined" label={`+${diff.toAdd.length} to ${currentTreeName}`} />
                      </Tooltip>
                    )}
                    {diff && !diff.tree && (
                      <Tooltip title="This CAP has no registered segment. Apply creates a new one.">
                        <Chip size="small" color="info" variant="outlined" label="New segment" />
                      </Tooltip>
                    )}
                    {(manualAddsByKey[seg.key] || []).length > 0 && (
                      <Tooltip title={`Manually added (pending apply): ${(manualAddsByKey[seg.key] || []).join(', ')}`}>
                        <Chip size="small" color="info" label={`+${manualAddsByKey[seg.key].length} manual`} />
                      </Tooltip>
                    )}
                    {seg.max_hop_depth > 0 && (
                      <Chip size="small" variant="outlined" label={`depth ${seg.max_hop_depth}`} />
                    )}
                    {seg.avg_rssi_dbm ? (
                      <Chip size="small" variant="outlined" label={`avg ${seg.avg_rssi_dbm} dBm`} />
                    ) : null}
                    {ceiling && (
                      <Tooltip title={CEILING_TOOLTIP}>
                        <Chip
                          size="small"
                          variant="outlined"
                          color={ceiling.fragile_nodes > 0 ? 'warning' : 'default'}
                          label={`ceiling ${ceiling.median_cost ?? '—'}/${ceiling.worst_cost ?? '—'}`}
                        />
                      </Tooltip>
                    )}
                    {stats.offline > 0 && (
                      <Tooltip title="These members are offline. Apply still works — the segment is written to their config and applied when they reconnect (tracked under Plans → Segment Plans).">
                        <Chip size="small" color="warning" variant="outlined" label={`${stats.offline} offline`} />
                      </Tooltip>
                    )}
                    {stats.conflicts > 0 && (
                      <Tooltip
                        title={(
                          <Box component="ul" sx={{ m: 0, pl: 2 }}>
                            {[...new Map(stats.otherSegments.map((s) => [s.treeId, s])).values()].map((s) => (
                              <li key={s.treeId}>
                                {s.name} (CAP {shortSerial(s.capSerial)}{s.segmentId ? ` · id ${s.segmentId}` : ''})
                              </li>
                            ))}
                          </Box>
                        )}
                      >
                        <Chip size="small" color="warning" variant="outlined" label={`${stats.conflicts} in other segment${stats.conflicts === 1 ? '' : 's'}`} />
                      </Tooltip>
                    )}
                    {(seg.advisories || []).length > 0 && (
                      <Tooltip
                        title={(
                          <Box component="ul" sx={{ m: 0, pl: 2 }}>
                            {seg.advisories.map((a, i) => (
                              <li key={a.code + (a.serial || '') + i}>{a.message}</li>
                            ))}
                          </Box>
                        )}
                      >
                        <Chip
                          size="small"
                          color="warning"
                          label={seg.advisories.length === 1 ? '1 advisory' : `${seg.advisories.length} advisories`}
                        />
                      </Tooltip>
                    )}
                  </Stack>
                  <Tooltip title={(() => {
                    if (applyBlockedByManualAssignments) return 'Assign excluded devices in the manual assignment table first.';
                    if (diff?.unchanged) return 'Already matches the registered segment.';
                    return '';
                  })()}
                  >
                    <span>
                      <Button
                        component="div"
                        role="button"
                        variant="outlined"
                        size="small"
                        sx={{ mr: 1 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleApplyOne(seg);
                        }}
                        disabled={busySegmentKey != null || applyAllBusy || applyBlockedByManualAssignments || !!diff?.unchanged}
                        startIcon={busySegmentKey === seg.key ? <CircularProgress size={14} color="inherit" /> : null}
                      >
                        Apply
                      </Button>
                    </span>
                  </Tooltip>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  {(seg.advisories || []).length > 0 && (
                    <Alert severity="warning" sx={{ m: 1.5, mb: 1 }}>
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {seg.advisories.map((a, i) => (
                          <li key={a.code + (a.serial || '') + i}>{a.message}</li>
                        ))}
                      </ul>
                    </Alert>
                  )}
                  {ceiling && (
                    <Tooltip title={CEILING_TOOLTIP}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ px: 2, pt: 1, display: 'block' }}
                      >
                        Path-cost ceiling: median {ceiling.median_cost ?? '—'}, worst {ceiling.worst_cost ?? '—'}
                        {ceiling.fragile_nodes > 0 ? ` · ${ceiling.fragile_nodes} lamp(s) with no backup path` : ''}
                      </Typography>
                    </Tooltip>
                  )}
                  {(seg.members || []).length === 0 ? (
                    <Typography variant="caption" color="text.secondary" sx={{ p: 2, display: 'block' }}>
                      CAP-only segment — no lamps assigned.
                    </Typography>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Lamp</TableCell>
                            <TableCell align="right">Hop depth</TableCell>
                            <TableCell>Connects via</TableCell>
                            <TableCell>Band</TableCell>
                            <TableCell align="right">
                              <Tooltip title={RSSI_RAW_TOOLTIP} placement="top">
                                <Box
                                  component="span"
                                  sx={{ cursor: 'help', borderBottom: '1px dotted', borderColor: 'text.disabled' }}
                                >
                                  RSSI↓ raw
                                </Box>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title={RSSI_ADJ_TOOLTIP} placement="top">
                                <Box
                                  component="span"
                                  sx={{ cursor: 'help', borderBottom: '1px dotted', borderColor: 'text.disabled' }}
                                >
                                  Adj. downlink
                                </Box>
                              </Tooltip>
                            </TableCell>
                            <TableCell>Current segment</TableCell>
                            <TableCell align="right">
                              <Tooltip title={HEADROOM_TOOLTIP} placement="top">
                                <Box
                                  component="span"
                                  sx={{ cursor: 'help', borderBottom: '1px dotted', borderColor: 'text.disabled' }}
                                >
                                  Headroom / why
                                </Box>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {seg.members.map((m) => {
                            const offline = connectedBySerial.get(normalizeSerial(m.serial)) === false;
                            const decision = decisionsBySerial.get(normalizeSerial(m.serial));
                            const targetTree = diff?.tree || null;
                            const existingSeg = otherExistingSegment(
                              existingSegmentBySerial,
                              m.serial,
                              targetTree,
                            );
                            const currentSeg = treeHasSerial(targetTree, m.serial)
                              ? treeSegmentInfo(targetTree)
                              : existingSegmentBySerial.get(normalizeSerial(m.serial));
                            const moveTarget = targetTree
                              ? `"${targetTree.name || targetTree.id}"${targetTree.segment_id ? ` (segment id ${targetTree.segment_id})` : ''}`
                              : `a new segment for CAP ${seg.cap_serials?.[0]}`;
                            return (
                              <TableRow key={m.serial}>
                                <TableCell sx={{ fontFamily: 'monospace' }}>
                                  {m.serial}
                                  {offline && (
                                    <Chip size="small" color="error" variant="outlined" label="offline" sx={{ ml: 1 }} />
                                  )}
                                </TableCell>
                                <TableCell align="right">{m.hop_depth}</TableCell>
                                <TableCell sx={{ fontFamily: 'monospace' }}>
                                  {m.parent_in_segment === seg.cap_serials?.[0]
                                    ? 'CAP (direct)'
                                    : (m.parent_in_segment || '—')}
                                </TableCell>
                                <TableCell>{formatBandLink(m.band, m.link_channel)}</TableCell>
                                <TableCell align="right">
                                  {formatRssi(m.rssi_down_raw_dbm)}
                                </TableCell>
                                <TableCell align="right">
                                  {formatRssi(m.rssi_dbm)}
                                </TableCell>
                                <TableCell>
                                  {existingSeg ? (
                                    <Tooltip title={`Registered as "${existingSeg.name}" (CAP ${existingSeg.capSerial}${existingSeg.segmentId ? `, segment id ${existingSeg.segmentId}` : ''}). Applying this proposal moves it to ${moveTarget}: once the rollout completes it is removed from "${existingSeg.name}" and the old segment id is stripped from its config.`}>
                                      <Chip
                                        size="small"
                                        color="warning"
                                        variant="outlined"
                                        label={existingSeg.name}
                                      />
                                    </Tooltip>
                                  ) : null}
                                  {!existingSeg && currentSeg && (
                                    <Tooltip title={`Already on "${currentSeg.name}"${currentSeg.segmentId ? ` (segment id ${currentSeg.segmentId})` : ''}, the segment this proposal applies to. No move needed.`}>
                                      <Chip size="small" variant="outlined" label={currentSeg.name} />
                                    </Tooltip>
                                  )}
                                  {!existingSeg && !currentSeg && (
                                    <Tooltip title="Not in any registered segment. Apply adds it.">
                                      <Typography variant="caption" color="text.secondary">None</Typography>
                                    </Tooltip>
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  <DecisionBadges decision={decision} />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Paper>
      )}

      {/* Devices excluded from RF planning (no golden neighbour report) */}
      {result && neighborDataPartial && missingNeighborManualList.length > 0 && segments.length > 0 && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderColor: 'warning.main' }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'warning.dark' }}>
            Manual assignment required ({missingNeighborManualList.length})
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            These devices had no golden neighbour report and were excluded from auto-segmentation.
            Pick a segment for each one so it is included when you apply that segment. This is a best-effort
            placement only — without neighbour data the planner cannot verify parent/child relationships.
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Device</TableCell>
                  <TableCell>Assign to segment</TableCell>
                  <TableCell align="right">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {missingNeighborManualList.map((serial) => {
                  const chosen = manualAssignments[serial];
                  const offline = connectedBySerial.get(normalizeSerial(serial)) === false;
                  return (
                    <TableRow key={serial}>
                      <TableCell sx={{ fontFamily: 'monospace' }}>
                        {serial}
                        {offline && (
                          <Chip size="small" color="error" variant="outlined" label="offline" sx={{ ml: 1 }} />
                        )}
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" fullWidth sx={{ minWidth: 220 }}>
                          <Select
                            displayEmpty
                            value={chosen?.segment_key || ''}
                            onChange={(e) => {
                              const key = e.target.value;
                              if (!key) {
                                clearUnassigned(serial);
                                return;
                              }
                              assignMissingToSegment(serial, key);
                            }}
                          >
                            <MenuItem value="">
                              <em>Not assigned</em>
                            </MenuItem>
                            {segments.map((seg) => (
                              <MenuItem key={seg.key} value={seg.key}>
                                Segment {shortSerial(seg.cap_serials?.[0])}
                                {' '}
                                ({seg.member_count ?? seg.members?.length ?? 0} lamps)
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell align="right">
                        {chosen ? (
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                            <Chip
                              size="small"
                              color="warning"
                              label={`→ ${shortSerial(chosen.cap_serial)}`}
                            />
                            <Button color="inherit" size="small" onClick={() => clearUnassigned(serial)}>
                              Clear
                            </Button>
                          </Stack>
                        ) : (
                          <Chip size="small" color="warning" variant="outlined" label="needs segment" />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Unassigned */}
      {result && unassignedList.length > 0 && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Unassigned lamps ({unassignedList.length})
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            These lamps were not auto-assigned (no link above the min-RSSI gate). Click a lamp to see the
            segments it could join — with hop count and estimated RSSI — and assign it to the one you prefer.
            Manual assignments are included when you apply that segment.
          </Typography>

          {unassignedList.map((u) => {
            const chosen = manualAssignments[u.serial];
            const options = u.options || [];
            const offline = connectedBySerial.get(normalizeSerial(u.serial)) === false;
            return (
              <Accordion key={u.serial} disableGutters variant="outlined" sx={{ mb: 0.5 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                      {u.serial}
                    </Typography>
                    {offline && <Chip size="small" color="error" variant="outlined" label="offline" />}
                    {chosen ? (
                      <Chip
                        size="small"
                        color="info"
                        label={`→ segment ${shortSerial(chosen.cap_serial)} · hop ${chosen.hop_depth}`}
                      />
                    ) : options.length > 0 ? (
                      <Chip size="small" variant="outlined" label={`${options.length} option${options.length > 1 ? 's' : ''}`} />
                    ) : (
                      <Chip size="small" color="warning" variant="outlined" label="no reachable segment" />
                    )}
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  {chosen && (
                    <Alert
                      severity="info"
                      sx={{ m: 1.5, mb: 1 }}
                      action={<Button color="inherit" size="small" onClick={() => clearUnassigned(u.serial)}>Undo</Button>}
                    >
                      Will be added to segment <strong>{chosen.cap_serial}</strong> (via {chosen.parent_serial === chosen.cap_serial ? 'CAP directly' : chosen.parent_serial}, hop {chosen.hop_depth}) when you apply that segment.
                    </Alert>
                  )}
                  {options.length === 0 ? (
                    <Typography variant="caption" color="text.secondary" sx={{ p: 2, display: 'block' }}>
                      No in-segment parent is reachable from neighbour data (including links below the RF floor).
                      Run a fresh neighbour scan, lower the min-RSSI gate, or add a CAP closer to this lamp.
                    </Typography>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Segment (CAP)</TableCell>
                            <TableCell>Connect via</TableCell>
                            <TableCell align="right">Hop depth</TableCell>
                            <TableCell>Band</TableCell>
                            <TableCell align="right">Est. RSSI</TableCell>
                            <TableCell align="right">
                              <Tooltip title={AFFINITY_TOOLTIP} placement="top">
                                <Box
                                  component="span"
                                  sx={{ cursor: 'help', borderBottom: '1px dotted', borderColor: 'text.disabled' }}
                                >
                                  Affinity
                                </Box>
                              </Tooltip>
                            </TableCell>
                            <TableCell>Why not auto-assigned</TableCell>
                            <TableCell align="right">Assign</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {options.map((opt) => {
                            const isChosen = chosen
                              && chosen.segment_key === opt.segment_key
                              && chosen.parent_serial === opt.parent_serial;
                            return (
                              <TableRow key={`${opt.segment_key}:${opt.parent_serial}`} hover selected={isChosen}>
                                <TableCell sx={{ fontFamily: 'monospace' }}>{shortSerial(opt.cap_serial)}</TableCell>
                                <TableCell sx={{ fontFamily: 'monospace' }}>
                                  {opt.parent_serial === opt.cap_serial ? 'CAP (direct)' : shortSerial(opt.parent_serial)}
                                </TableCell>
                                <TableCell align="right">{opt.hop_depth}</TableCell>
                                <TableCell>{opt.band || '—'}</TableCell>
                                <TableCell align="right">
                                  {Number.isFinite(opt.rssi_dbm) ? (
                                    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                                      <span>{Math.round(opt.rssi_dbm)} dBm</span>
                                      {opt.below_gate && (
                                        <Tooltip title="Below the min-RSSI gate — this is why the lamp was left unassigned. You can still assign it, but the link is marginal.">
                                          <Chip size="small" color="warning" variant="outlined" label="weak" />
                                        </Tooltip>
                                      )}
                                    </Stack>
                                  ) : '—'}
                                </TableCell>
                                <TableCell align="right">{(opt.affinity ?? 0).toFixed(3)}</TableCell>
                                <TableCell>
                                  {opt.not_assignable_reason ? (
                                    <Tooltip title={opt.not_assignable_reason}>
                                      <Chip
                                        size="small"
                                        color="default"
                                        variant="outlined"
                                        label={notAssignableLabel(opt.not_assignable_code)}
                                      />
                                    </Tooltip>
                                  ) : opt.below_gate ? (
                                    <Tooltip title="Link is an eligible candidate but its RSSI is below the min-RSSI membership gate — the auto-planner skipped it. You can still assign it manually.">
                                      <Chip size="small" color="warning" variant="outlined" label="Below RSSI gate" />
                                    </Tooltip>
                                  ) : (
                                    <Typography variant="caption" color="text.secondary">—</Typography>
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  <Button
                                    size="small"
                                    variant={isChosen ? 'contained' : 'outlined'}
                                    onClick={() => assignUnassigned(u.serial, opt)}
                                  >
                                    {isChosen ? 'Selected' : 'Assign'}
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Paper>
      )}

      {result && segments.length === 0 && (
        <Alert severity="warning">
          No segments could be proposed — no CAPs found in the site, or no feasible links above the RF floor.
        </Alert>
      )}

      {!result && !loading && (
        <Alert severity="info">
          Click <strong>Run</strong> to partition the site into CAP-centric segments. Each segment can then be
          applied as a custom tree (writing segment_id_dev / segment_id_ap to its members).
        </Alert>
      )}

      {/* Load-plan menu */}
      <Menu
        anchorEl={loadMenuAnchor}
        open={Boolean(loadMenuAnchor)}
        onClose={() => setLoadMenuAnchor(null)}
      >
        {loadingProposals && (
          <MenuItem disabled>
            <CircularProgress size={16} sx={{ mr: 1 }} /> Loading…
          </MenuItem>
        )}
        {!loadingProposals && savedProposals.length === 0 && (
          <MenuItem disabled>No saved plans</MenuItem>
        )}
        {!loadingProposals && savedProposals.map((p) => (
          <MenuItem key={p.id} onClick={() => handleLoadProposal(p.id)} sx={{ pr: 1 }}>
            <Box sx={{ flexGrow: 1, mr: 2 }}>
              <Typography variant="body2">{p.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {p.segment_count ?? 0} seg · {p.assigned_count ?? 0} assigned
                {p.unassigned_count ? ` · ${p.unassigned_count} unassigned` : ''}
              </Typography>
            </Box>
            <IconButton
              size="small"
              edge="end"
              onClick={(e) => { e.stopPropagation(); handleDeleteProposal(p.id, p.name); }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </MenuItem>
        ))}
      </Menu>

      {/* Save-plan dialog */}
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Save segmentation plan</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Saves the current proposal — segments, manual assignments, and settings — so you can reload it later
            before applying.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Plan name"
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && saveName.trim()) handleSaveProposal(); }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!saveName.trim() || savingProposal}
            startIcon={savingProposal ? <CircularProgress size={16} color="inherit" /> : null}
            onClick={handleSaveProposal}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={partialConfirmOpen} onClose={() => setPartialConfirmOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Continue without neighbour data?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Alert severity="error" sx={{ mb: 2 }}>
              <AlertTitle>This may produce an inaccurate segmentation</AlertTitle>
              Devices without a golden neighbour report are <strong>excluded from RF planning</strong>.
              The auto-generated segments will not reflect how those devices actually hear each other or
              which CAP they should belong to.
            </Alert>
            <Typography variant="body2" sx={{ mb: 1 }}>
              You will need to <strong>manually assign</strong> each excluded device to a segment before
              applying. Use this only to unblock rollout when scans are not possible right now.
            </Typography>
            <Typography variant="body2">
              Recommended: scan the missing devices, mark reports as golden, then re-run segmentation
              with complete data.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPartialConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={confirmPartialRun}>
            Run with partial data
          </Button>
        </DialogActions>
      </Dialog>
      </Box>

      <Box sx={{ display: innerTab === 'audit' ? 'block' : 'none' }}>
        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" sx={{ gap: 1 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Segment audit
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Choose one scan type, then scan the site. Fix config issues or assign unsegmented
                devices to an existing segment.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              {auditMode !== AUDIT_MODE_UNASSIGNED && auditResult?.devices?.length > 0 && (
                <Button
                  size="small"
                  color="warning"
                  variant="contained"
                  startIcon={<CleaningServicesIcon />}
                  disabled={applyingSerials.size > 0 || auditLoading}
                  onClick={() => applyAuditSerials(
                    auditResult.devices.filter((d) => !d.in_active_plan).map((d) => d.serial),
                  )}
                >
                  Apply all ({auditResult.devices.filter((d) => !d.in_active_plan).length})
                </Button>
              )}
              <Button
                size="small"
                variant="outlined"
                onClick={runAudit}
                disabled={auditLoading}
                startIcon={auditLoading ? <CircularProgress size={14} color="inherit" /> : null}
              >
                {auditResult ? 'Re-scan' : 'Scan'}
              </Button>
            </Stack>
          </Stack>

          <FormControl component="fieldset" sx={{ mt: 1.5, width: '100%' }}>
            <RadioGroup
              value={auditMode}
              onChange={(e) => {
                setAuditMode(e.target.value);
                setAuditResult(null);
                setAssignAllTreeId('');
                setAssignTreeBySerial({});
                setAuditFixChoices({});
              }}
            >
              {AUDIT_MODES.map((opt) => (
                <FormControlLabel
                  key={opt.value}
                  value={opt.value}
                  control={<Radio size="small" />}
                  sx={{ alignItems: 'flex-start', mb: 0.5 }}
                  label={(
                    <Box>
                      <Typography variant="body2">{opt.label}</Typography>
                      <Typography variant="caption" color="text.secondary">{opt.help}</Typography>
                    </Box>
                  )}
                />
              ))}
            </RadioGroup>
          </FormControl>

          {auditMode === AUDIT_MODE_UNASSIGNED
            && auditResult
            && (auditResult.devices || []).length > 0
            && registeredSegments.length > 0 && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mt: 1.5 }}>
              <FormControl size="small" sx={{ minWidth: 240, flex: 1 }}>
                <Select
                  displayEmpty
                  value={assignAllTreeId}
                  onChange={(e) => setAssignAllTreeId(e.target.value)}
                >
                  <MenuItem value="" disabled>Add all to segment…</MenuItem>
                  {registeredSegments.map((seg) => (
                    <MenuItem key={seg.id} value={seg.id}>
                      {seg.name} (segment {seg.segment_id})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                size="small"
                variant="contained"
                disabled={!assignAllTreeId || applyingSerials.size > 0}
                onClick={() => assignToSegment(
                  auditResult.devices.map((d) => d.serial),
                  assignAllTreeId,
                )}
              >
                Add all ({auditResult.devices.length})
              </Button>
            </Stack>
          )}

          {auditMode === AUDIT_MODE_UNASSIGNED
            && auditResult
            && (auditResult.devices || []).length > 0
            && registeredSegments.length === 0 && (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              No registered segments yet — create a segment first before assigning devices.
            </Alert>
          )}

          {auditResult && (auditResult.devices || []).length === 0 && (
            <Alert severity="success" sx={{ mt: 1.5 }}>
              {auditMode === AUDIT_MODE_UNASSIGNED && `All site devices belong to a registered segment (${auditResult.total_scanned ?? 0} scanned).`}
              {auditMode === AUDIT_MODE_MEMBERSHIP && `Every device config matches its registered segments (${auditResult.total_scanned ?? 0} scanned).`}
              {auditMode !== AUDIT_MODE_UNASSIGNED && auditMode !== AUDIT_MODE_MEMBERSHIP
                && `No segment config issues found (${auditResult.total_scanned ?? 0} scanned).`}
            </Alert>
          )}

          {auditResult && (auditResult.devices || []).length > 0 && auditMode === AUDIT_MODE_UNASSIGNED && (
            <TableContainer sx={{ mt: 1.5 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Device</TableCell>
                    <TableCell align="right">Add to segment</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {auditResult.devices.map((d) => {
                    const busy = applyingSerials.has(d.serial);
                    const treeId = assignTreeBySerial[d.serial] || '';
                    return (
                      <TableRow key={d.serial} hover>
                        <TableCell sx={{ fontFamily: 'monospace' }}>
                          {d.serial}
                          {d.is_cap && <Chip size="small" variant="outlined" label="CAP" sx={{ ml: 1 }} />}
                          {d.connected === false && (
                            <Chip size="small" color="error" variant="outlined" label="offline" sx={{ ml: 1 }} />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                            <FormControl size="small" sx={{ minWidth: 180 }}>
                              <Select
                                displayEmpty
                                value={treeId}
                                disabled={registeredSegments.length === 0}
                                onChange={(e) => {
                                  setAssignTreeBySerial((prev) => ({
                                    ...prev,
                                    [d.serial]: e.target.value,
                                  }));
                                }}
                              >
                                <MenuItem value="" disabled>Select segment…</MenuItem>
                                {registeredSegments.map((seg) => (
                                  <MenuItem key={seg.id} value={seg.id}>
                                    {seg.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={busy || !treeId}
                              startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}
                              onClick={() => assignToSegment([d.serial], treeId)}
                            >
                              Add
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {auditResult && (auditResult.devices || []).length > 0 && auditMode === AUDIT_MODE_MEMBERSHIP && (
            <TableContainer sx={{ mt: 1.5 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Device</TableCell>
                    <TableCell>Mismatches</TableCell>
                    <TableCell>Config segment IDs</TableCell>
                    <TableCell align="right">Fix</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {auditResult.devices.map((d) => {
                    const busy = applyingSerials.has(d.serial);
                    const diffs = d.membership_diffs || [];
                    return (
                      <TableRow key={d.serial} hover>
                        <TableCell sx={{ fontFamily: 'monospace', verticalAlign: 'top' }}>
                          {d.serial}
                          {d.is_cap && <Chip size="small" variant="outlined" label="CAP" sx={{ ml: 1 }} />}
                          {d.connected === false && (
                            <Chip size="small" color="error" variant="outlined" label="offline" sx={{ ml: 1 }} />
                          )}
                          {d.in_active_plan && (
                            <Chip size="small" color="info" variant="outlined" label="in active plan" sx={{ ml: 1 }} />
                          )}
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top' }}>
                          <Stack direction="column" spacing={0.5}>
                            {d.multiple_trees && (
                              <Alert severity="warning" sx={{ py: 0 }}>
                                Listed in {d.trees.length} segments: {membershipTreeNames(d.trees)}
                              </Alert>
                            )}
                            {diffs.map((diff) => {
                              const key = String(diff.segment_id);
                              const choice = auditFixChoices[d.serial]?.membership?.[key] || MEMBERSHIP_KEEP_TREE;
                              const labels = membershipOptionLabels(diff);
                              return (
                                <Box key={`m${key}`} sx={{ mb: 0.5 }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    {membershipDiffText(diff)}
                                  </Typography>
                                  <RadioGroup
                                    value={choice}
                                    onChange={(e) => {
                                      setAuditFixChoices((prev) => ({
                                        ...prev,
                                        [d.serial]: {
                                          ...prev[d.serial],
                                          membership: {
                                            ...(prev[d.serial]?.membership || {}),
                                            [key]: e.target.value,
                                          },
                                        },
                                      }));
                                    }}
                                  >
                                    <FormControlLabel
                                      value={MEMBERSHIP_KEEP_TREE}
                                      control={<Radio size="small" />}
                                      label={<Typography variant="caption">{labels.tree}</Typography>}
                                    />
                                    <FormControlLabel
                                      value={MEMBERSHIP_KEEP_DEVICE}
                                      disabled={membershipKeepDeviceDisabled(diff)}
                                      control={<Radio size="small" />}
                                      label={<Typography variant="caption">{labels.device}</Typography>}
                                    />
                                    <FormControlLabel
                                      value={MEMBERSHIP_SKIP}
                                      control={<Radio size="small" />}
                                      label={<Typography variant="caption">Skip</Typography>}
                                    />
                                  </RadioGroup>
                                </Box>
                              );
                            })}
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', color: 'text.secondary', verticalAlign: 'top' }}>
                          {(d.segment_ids || []).join(', ') || '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                          <Tooltip title={(() => {
                            if (d.in_active_plan) return 'A segment plan is rolling out to this device; wait for it to finish.';
                            if (d.connected === false) return 'Saves the selected fixes; the device receives its config on reconnect.';
                            return 'Applies the selected fixes and pushes any config change to the device now.';
                          })()}
                          >
                            <span>
                              <Button
                                size="small"
                                color="warning"
                                variant="outlined"
                                disabled={busy || d.in_active_plan || diffs.length === 0}
                                startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}
                                onClick={() => applyAuditSerials([d.serial])}
                              >
                                Apply
                              </Button>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {auditResult && (auditResult.devices || []).length > 0
            && auditMode !== AUDIT_MODE_UNASSIGNED && auditMode !== AUDIT_MODE_MEMBERSHIP && (
            <TableContainer sx={{ mt: 1.5 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Device</TableCell>
                    <TableCell>Issues</TableCell>
                    <TableCell>All segment IDs</TableCell>
                    <TableCell align="right">Fix</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {auditResult.devices.map((d) => {
                    const busy = applyingSerials.has(d.serial);
                    const unknown = d.unknown_segment_ids || [];
                    const devNotInAp = d.dev_not_in_ap || [];
                    const apNotInDev = d.ap_not_in_dev || [];
                    const apWildcards = d.ap_wildcard_ids || [];
                    return (
                      <TableRow key={d.serial} hover>
                        <TableCell sx={{ fontFamily: 'monospace' }}>
                          {d.serial}
                          {d.is_cap && <Chip size="small" variant="outlined" label="CAP" sx={{ ml: 1 }} />}
                          {d.connected === false && (
                            <Chip size="small" color="error" variant="outlined" label="offline" sx={{ ml: 1 }} />
                          )}
                        </TableCell>
                        <TableCell>
                          <Stack direction="column" spacing={0.5}>
                            {unknown.length > 0 && (
                              <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" sx={{ gap: 0.5 }}>
                                <Tooltip title="Not registered in NMS — will be removed from this device's config.">
                                  <Typography variant="caption" sx={{ minWidth: 90 }}>unknown (remove):</Typography>
                                </Tooltip>
                                {unknown.map((id) => (
                                  <Chip key={`u${id}`} size="small" color="warning" label={id} />
                                ))}
                              </Stack>
                            )}
                            {devNotInAp.length > 0 && devNotInAp.map((id) => {
                              const key = String(id);
                              const choice = auditFixChoices[d.serial]?.devNotInAp?.[key] || AUDIT_FIX_ADD_AP;
                              return (
                                <Box key={`da${id}`} sx={{ mb: 0.5 }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    ID {id} in dev, missing from AP:
                                  </Typography>
                                  <RadioGroup
                                    row
                                    value={choice}
                                    onChange={(e) => {
                                      setAuditFixChoices((prev) => ({
                                        ...prev,
                                        [d.serial]: {
                                          ...prev[d.serial],
                                          devNotInAp: {
                                            ...(prev[d.serial]?.devNotInAp || {}),
                                            [key]: e.target.value,
                                          },
                                        },
                                      }));
                                    }}
                                  >
                                    <FormControlLabel
                                      value={AUDIT_FIX_ADD_AP}
                                      control={<Radio size="small" />}
                                      label={<Typography variant="caption">Add to AP (default)</Typography>}
                                    />
                                    <FormControlLabel
                                      value={AUDIT_FIX_REMOVE_DEV}
                                      control={<Radio size="small" />}
                                      label={<Typography variant="caption">Remove from dev</Typography>}
                                    />
                                  </RadioGroup>
                                </Box>
                              );
                            })}
                            {apNotInDev.length > 0 && apNotInDev.map((id) => {
                              const key = String(id);
                              const choice = auditFixChoices[d.serial]?.apNotInDev?.[key] || AUDIT_FIX_ADD_DEV;
                              return (
                                <Box key={`ad${id}`} sx={{ mb: 0.5 }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    ID {id} in AP, missing from dev:
                                  </Typography>
                                  <RadioGroup
                                    row
                                    value={choice}
                                    onChange={(e) => {
                                      setAuditFixChoices((prev) => ({
                                        ...prev,
                                        [d.serial]: {
                                          ...prev[d.serial],
                                          apNotInDev: {
                                            ...(prev[d.serial]?.apNotInDev || {}),
                                            [key]: e.target.value,
                                          },
                                        },
                                      }));
                                    }}
                                  >
                                    <FormControlLabel
                                      value={AUDIT_FIX_ADD_DEV}
                                      control={<Radio size="small" />}
                                      label={<Typography variant="caption">Add to dev (default)</Typography>}
                                    />
                                    <FormControlLabel
                                      value={AUDIT_FIX_REMOVE_AP}
                                      control={<Radio size="small" />}
                                      label={<Typography variant="caption">Remove from AP</Typography>}
                                    />
                                  </RadioGroup>
                                </Box>
                              );
                            })}
                            {apWildcards.length > 0 && (
                              <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" sx={{ gap: 0.5 }}>
                                <Tooltip title="0xFEFE / 0xFFFF in segment_id_ap — will be removed from segment_id_ap only.">
                                  <Typography variant="caption" sx={{ minWidth: 90 }}>AP wildcard:</Typography>
                                </Tooltip>
                                {apWildcards.map((id) => (
                                  <Chip
                                    key={`w${id}`}
                                    size="small"
                                    color="secondary"
                                    variant="outlined"
                                    label={`0x${id.toString(16).toUpperCase()}`}
                                  />
                                ))}
                              </Stack>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                          {(d.segment_ids || []).join(', ') || '—'}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={d.connected === false
                            ? 'Applies your selected fixes to the persisted config; the device receives it on reconnect. Unknown segment IDs are always removed.'
                            : 'Applies your selected fixes and pushes the corrected config to the device now. Unknown segment IDs are always removed.'}
                          >
                            <span>
                              <Button
                                size="small"
                                color="warning"
                                variant="outlined"
                                disabled={busy}
                                startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}
                                onClick={() => applyAuditSerials([d.serial])}
                              >
                                Apply
                              </Button>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default SegmentPanel;
